import { EntityByEntityTypeId, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { LucideIcon } from "../../icon/LucideIcon";
import { EntityConstants } from "../../../api/GenericTypes";
import { Routes } from "../../routes/useEntityRoutes";
import { IGenericEntityPermissions } from "../../permissions/useEntityPermissions";
import { Link } from "react-router-dom";

interface EntityCloneButtonProps<EntityTypeId extends GenericEntityConstantsEntities> {
  entity: EntityByEntityTypeId<EntityTypeId>;
  entityConstants: EntityConstants;
  routes: Routes;
  permissions: IGenericEntityPermissions<EntityTypeId>;
  disabledOverride?: boolean;
}

/**
 * Renders a button that allows users to clone an entity.
 *
 * @template EntityTypeId - The type of the entity identifier, extending `GenericEntityConstantsEntities`.
 * @param props - The props for the EntityCloneButton component.
 * @param props.entity - The entity object to be cloned.
 * @param props.entityConstants - Constants related to the entity, such as singular/plural names.
 * @param props.routes - An object containing route-generating functions, including `getCloneLink`.
 * @property permissions - Permission utilities for the entity, including `canEdit`.
 * @returns A `LinkButton` component that navigates to the clone page for the entity.
 */
export const EntityCloneButton = <EntityTypeId extends GenericEntityConstantsEntities>({
  entity,
  entityConstants,
  routes,
  permissions,
  disabledOverride,
}: EntityCloneButtonProps<EntityTypeId>) => {
  const disabled = disabledOverride ? disabledOverride : !permissions.canCreate;

  if (disabled)
    return (
      <button
        className="btn btn-sm btn-ghost-secondary disabled"
        title={`Clone this ${entityConstants.entitySingular}`}
        disabled
      >
        <LucideIcon name="copy" /> Clone
      </button>
    );

  return (
    <Link
      to={routes.getCloneLink(+entity.id)}
      className={`btn btn-sm btn-ghost-secondary ${disabled ? "disabled" : ""}`}
      title={`Clone this ${entityConstants.entitySingular}`}
      style={{ textAlign: "left" }}
    >
      <LucideIcon name="copy" /> Clone
    </Link>
  );
};
